import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Heart, 
  Users, 
  Clock, 
  Shield, 
  Award,
  Stethoscope,
  Building2,
  CheckCircle,
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  Globe
} from 'lucide-react';
import {
  clinicService,
  contentService,
  doctorService,
  hospitalService,
} from '@/services/api';

const readNumber = (value) => {
  if (value === null || value === undefined || value === false || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const firstNumber = (...values) => {
  for (const value of values) {
    const parsed = readNumber(value);
    if (parsed !== null) {
      return parsed;
    }
  }
  return null;
};

const readStatFromSettings = (settings, keys) => {
  const statistics = settings?.statistics || settings?.stats || {};

  for (const key of keys) {
    const parsed = firstNumber(statistics[key], settings?.[key]);
    if (parsed !== null) {
      return parsed;
    }
  }

  return null;
};

const readResponseTotal = (response, collectionKeys = []) => {
  const body = response?.data || {};
  const data = body?.data || {};
  const pagination = data?.pagination || body?.pagination || {};
  const meta = data?.meta || body?.meta || {};

  const directTotal = firstNumber(
    body.total,
    body.count,
    data.total,
    data.count,
    pagination.total,
    meta.total
  );
  if (directTotal !== null) {
    return directTotal;
  }

  for (const key of collectionKeys) {
    const rows = data[key] || body[key];
    if (Array.isArray(rows)) {
      return rows.length;
    }
  }

  if (Array.isArray(data)) {
    return data.length;
  }
  if (Array.isArray(body)) {
    return body.length;
  }

  return null;
};

const resolvePlatformStats = ({
  settings,
  doctorsResponse,
  clinicsResponse,
  hospitalsResponse,
}) => {
  const patients = readStatFromSettings(settings, [
    'active_patients_count',
    'active_patients',
    'patients_count',
    'patients',
  ]);
  const doctors = firstNumber(
    readStatFromSettings(settings, [
      'certified_doctors_count',
      'certified_doctors',
      'doctors_count',
      'doctors',
    ]),
    readResponseTotal(doctorsResponse, ['doctors', 'items'])
  );
  const clinics = firstNumber(
    readStatFromSettings(settings, ['clinics_count', 'clinics']),
    readResponseTotal(clinicsResponse, ['clinics'])
  );
  const hospitals = firstNumber(
    readStatFromSettings(settings, ['hospitals_count', 'hospitals']),
    readResponseTotal(hospitalsResponse, ['hospitals'])
  );
  const facilities = firstNumber(
    readStatFromSettings(settings, [
      'clinics_and_hospitals_count',
      'clinics_and_hospitals',
      'facilities_count',
      'facilities',
    ]),
    clinics !== null || hospitals !== null
      ? (clinics || 0) + (hospitals || 0)
      : null
  );
  return {
    patients,
    doctors,
    facilities,
  };
};

const formatCountStat = (value) => {
  const parsed = readNumber(value);
  if (parsed === null) {
    return '-';
  }

  return new Intl.NumberFormat('en-US').format(Math.round(parsed));
};

const AboutPage = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'ar').toLowerCase().startsWith('ar') ? 'ar' : 'en';
  const isRTL = lang === 'ar';

  const [settings, setSettings] = useState(null);
  const [platformStats, setPlatformStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      try {
        const [
          settingsResult,
          doctorsResult,
          clinicsResult,
          hospitalsResult,
        ] = await Promise.allSettled([
          contentService.getSettings(),
          doctorService.getAllDoctors({ limit: 1 }),
          clinicService.getAllClinics({ limit: 1 }),
          hospitalService.getAllHospitals({ limit: 1 }),
        ]);

        const nextSettings =
          settingsResult.status === 'fulfilled'
            ? settingsResult.value.data?.data || null
            : null;

        if (settingsResult.status === 'rejected') {
          console.error('Error fetching settings:', settingsResult.reason);
        }

        setSettings(nextSettings);
        setPlatformStats(resolvePlatformStats({
          settings: nextSettings,
          doctorsResponse:
            doctorsResult.status === 'fulfilled' ? doctorsResult.value : null,
          clinicsResponse:
            clinicsResult.status === 'fulfilled' ? clinicsResult.value : null,
          hospitalsResponse:
            hospitalsResult.status === 'fulfilled' ? hospitalsResult.value : null,
        }));
      } catch (err) {
        console.error('Error fetching about page data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  const appName = settings?.app_name || 'Dactrha';
  const appDescription = settings?.app_short_description || (lang === 'ar' 
    ? 'منصة رائدة في مجال الرعاية الصحية الرقمية' 
    : 'A leading digital healthcare platform');

  const stats = [
    { 
      icon: Users, 
      value: formatCountStat(platformStats.patients),
      label: lang === 'ar' ? 'مريض نشط' : 'Active Patients',
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
    },
    { 
      icon: Stethoscope, 
      value: formatCountStat(platformStats.doctors),
      label: lang === 'ar' ? 'طبيب معتمد' : 'Certified Doctors',
      color: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400'
    },
    { 
      icon: Building2, 
      value: formatCountStat(platformStats.facilities),
      label: lang === 'ar' ? 'عيادة ومستشفى' : 'Clinics & Hospitals',
      color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
    },
  ];

  const values = [
    {
      icon: Heart,
      title: lang === 'ar' ? 'الرعاية أولاً' : 'Care First',
      description: lang === 'ar' 
        ? 'نضع صحة المريض في المقام الأول ونسعى لتوفير أفضل تجربة رعاية صحية'
        : 'We put patient health first and strive to provide the best healthcare experience'
    },
    {
      icon: Shield,
      title: lang === 'ar' ? 'الخصوصية والأمان' : 'Privacy & Security',
      description: lang === 'ar' 
        ? 'نحمي بياناتك الطبية بأعلى معايير الأمان والتشفير'
        : 'We protect your medical data with the highest security and encryption standards'
    },
    {
      icon: Clock,
      title: lang === 'ar' ? 'متاحون دائماً' : 'Always Available',
      description: lang === 'ar' 
        ? 'خدماتنا متاحة على مدار الساعة لضمان حصولك على الرعاية عند الحاجة'
        : 'Our services are available 24/7 to ensure you get care when you need it'
    },
    {
      icon: Award,
      title: lang === 'ar' ? 'جودة معتمدة' : 'Certified Quality',
      description: lang === 'ar' 
        ? 'جميع أطبائنا معتمدون ومرخصون وذوو خبرة عالية'
        : 'All our doctors are certified, licensed, and highly experienced'
    },
  ];

  const features = [
    lang === 'ar' ? 'حجز المواعيد الإلكتروني' : 'Online Appointment Booking',
    lang === 'ar' ? 'استشارات طبية عبر الفيديو' : 'Video Medical Consultations',
    lang === 'ar' ? 'السجلات الطبية الإلكترونية' : 'Electronic Medical Records',
    lang === 'ar' ? 'تذكيرات المواعيد الذكية' : 'Smart Appointment Reminders',
    lang === 'ar' ? 'البحث عن الأطباء حسب التخصص' : 'Search Doctors by Specialty',
    lang === 'ar' ? 'صيدلية إلكترونية' : 'Online Pharmacy',
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" dir={isRTL ? 'rtl' : 'ltr'} data-testid="about-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-white/20 text-white mb-4">
            {lang === 'ar' ? 'من نحن' : 'About Us'}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {lang === 'ar' ? `مرحباً بك في ${appName}` : `Welcome to ${appName}`}
          </h1>
          <p className="text-xl text-teal-100 max-w-3xl mx-auto mb-8">
            {lang === 'ar' 
              ? 'نحن منصة رائدة في مجال الرعاية الصحية الرقمية، نربط المرضى بأفضل الأطباء والمتخصصين في المنطقة'
              : 'We are a leading digital healthcare platform, connecting patients with the best doctors and specialists in the region'}
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/doctors">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50">
                {lang === 'ar' ? 'ابحث عن طبيب' : 'Find a Doctor'}
                <ArrowRight className={`h-4 w-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                {lang === 'ar' ? 'تواصل معنا' : 'Contact Us'}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="container mx-auto px-4 -mt-12">
        <div className="grid md:grid-cols-3 gap-4">
          {loading ? (
            Array.from({ length: 3 }).map((_, idx) => (
              <Card key={idx} className="dark:bg-slate-800 border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <Skeleton className="w-12 h-12 rounded-full mx-auto mb-4" />
                  <Skeleton className="h-8 w-20 mx-auto mb-2" />
                  <Skeleton className="h-4 w-24 mx-auto" />
                </CardContent>
              </Card>
            ))
          ) : (
            stats.map((stat, idx) => (
              <Card key={idx} className="dark:bg-slate-800 border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className={`w-12 h-12 rounded-full ${stat.color} flex items-center justify-center mx-auto mb-4`}>
                    <stat.icon className="h-6 w-6" />
                  </div>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white mb-1">{stat.value}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{stat.label}</p>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Our Story Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 mb-4">
              {lang === 'ar' ? 'قصتنا' : 'Our Story'}
            </Badge>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">
              {lang === 'ar' 
                ? 'نبني مستقبل الرعاية الصحية' 
                : 'Building the Future of Healthcare'}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 mb-4 leading-relaxed">
              {lang === 'ar'
                ? 'تأسست داكترها بهدف تحويل تجربة الرعاية الصحية وجعلها أكثر سهولة وملاءمة للجميع. نحن نؤمن بأن كل شخص يستحق الحصول على رعاية صحية عالية الجودة.'
                : 'Dactrha was founded with the goal of transforming the healthcare experience and making it more accessible and convenient for everyone. We believe that everyone deserves access to high-quality healthcare.'}
            </p>
            <p className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
              {lang === 'ar'
                ? 'من خلال منصتنا الرقمية المبتكرة، نربط المرضى بشبكة واسعة من الأطباء والمتخصصين المعتمدين، مما يتيح لهم الحصول على الرعاية التي يحتاجونها في الوقت والمكان المناسبين.'
                : 'Through our innovative digital platform, we connect patients with a wide network of certified doctors and specialists, enabling them to get the care they need at the right time and place.'}
            </p>
            
            {/* Features List */}
            <div className="grid grid-cols-2 gap-3">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="aspect-square bg-gradient-to-br from-teal-100 to-teal-200 dark:from-teal-900/30 dark:to-teal-800/30 rounded-2xl overflow-hidden">
              <img
                src="/images/about-healthcare-team.png"
                alt={lang === 'ar' ? 'فريق رعاية صحية' : 'Healthcare team'}
                className="h-full w-full object-cover"
              />
            </div>
            <Card className="absolute -bottom-6 -right-6 dark:bg-slate-800 border-0 shadow-lg max-w-xs hidden lg:block">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
                    <Award className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">
                      {lang === 'ar' ? 'منصة معتمدة' : 'Certified Platform'}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {lang === 'ar' ? 'معايير عالمية' : 'Global Standards'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="bg-slate-100 dark:bg-slate-800/50 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 mb-4">
              {lang === 'ar' ? 'قيمنا' : 'Our Values'}
            </Badge>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">
              {lang === 'ar' ? 'ما نؤمن به' : 'What We Believe In'}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              {lang === 'ar'
                ? 'قيمنا الأساسية التي توجه كل قرار نتخذه وكل خدمة نقدمها'
                : 'Our core values that guide every decision we make and every service we provide'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => (
              <Card key={idx} className="dark:bg-slate-800 border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-7 w-7 text-teal-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">{value.title}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-4 py-16">
        <Card className="bg-gradient-to-r from-teal-600 to-teal-700 border-0 text-white overflow-hidden">
          <CardContent className="p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">
              {lang === 'ar' ? 'مستعد لتجربة رعاية صحية أفضل؟' : 'Ready for a Better Healthcare Experience?'}
            </h2>
            <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
              {lang === 'ar'
                ? 'انضم إلى آلاف المرضى الذين يثقون في داكترها للحصول على رعايتهم الصحية'
                : 'Join thousands of patients who trust Dactrha for their healthcare needs'}
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50">
                  {lang === 'ar' ? 'سجل الآن مجاناً' : 'Sign Up for Free'}
                </Button>
              </Link>
              <Link to="/doctors">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  {lang === 'ar' ? 'تصفح الأطباء' : 'Browse Doctors'}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AboutPage;
